import { useEffect, useMemo, useRef } from 'react';
import type { MouseEvent, PointerEvent } from 'react';

/**
 * Замена `InkWell.onLongPress`: у DOM такого события нет.
 *
 * Во Flutter долгое нажатие узнаёт `LongPressGestureRecognizer`: он ждёт
 * `kLongPressTimeout` (500 мс) и отменяется, как только палец уехал дальше
 * `kTouchSlop` (18 px). Оба правила перенесены сюда как есть, а слушаются
 * pointer-события — они одинаково приходят и от касания, и от мыши, и от
 * пера, в отличие от пары touch/mouse.
 *
 * Два отличия от Flutter, оба вынужденные:
 *
 * 1. Во Flutter арена жестов сама решает, что сработало — долгое нажатие
 *    или тап, и `onTap` после долгого уже не приходит. В вебе `click`
 *    приходит всегда, поэтому первый `click` после сработавшего долгого
 *    нажатия гасится вручную: иначе long-press по планшету и открыл бы
 *    панель пароля, и сразу подключился бы к нему.
 * 2. Долгое касание открывает контекстное меню браузера — во Flutter
 *    никакого меню не было, поэтому оно гасится.
 */

/** `kLongPressTimeout` из Flutter. */
const LONG_PRESS_TIMEOUT_MS = 500;

/** `kTouchSlop` из Flutter: дрожание пальца до 18 px нажатие не отменяет. */
const TOUCH_SLOP_PX = 18;

/** Левая кнопка мыши; у касания и пера `button` при нажатии тоже 0. */
const PRIMARY_BUTTON = 0;

export interface UseLongPressOptions {
  /** `InkWell.onLongPress`. `null` — долгого нажатия у элемента нет. */
  readonly onLongPress?: (() => void) | null;

  /** `InkWell.onTap`. */
  readonly onTap?: (() => void) | null;
}

/** Набор обработчиков, который целиком раскрывается на элементе. */
export interface LongPressHandlers {
  readonly onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerMove: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerUp: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerCancel: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerLeave: (event: PointerEvent<HTMLElement>) => void;
  readonly onContextMenu: (event: MouseEvent<HTMLElement>) => void;
  readonly onClick: (event: MouseEvent<HTMLElement>) => void;
}

export function useLongPress({ onLongPress, onTap }: UseLongPressOptions): LongPressHandlers {
  const timerRef = useRef<number | null>(null);
  const startRef = useRef<Readonly<{ x: number; y: number }> | null>(null);

  /** Долгое нажатие уже сработало — следующий `click` не в счёт. */
  const firedRef = useRef(false);

  const handlers = useMemo<LongPressHandlers>(() => {
    const cancelTimer = (): void => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = null;
      startRef.current = null;
    };

    return {
      onPointerDown: (event) => {
        // Не первый палец и не левая кнопка мыши жестом не считаются.
        if (!event.isPrimary || event.button !== PRIMARY_BUTTON) return;
        cancelTimer();
        firedRef.current = false;
        if (onLongPress == null) return;
        startRef.current = { x: event.clientX, y: event.clientY };
        timerRef.current = window.setTimeout(() => {
          timerRef.current = null;
          startRef.current = null;
          firedRef.current = true;
          onLongPress();
        }, LONG_PRESS_TIMEOUT_MS);
      },

      onPointerMove: (event) => {
        const start = startRef.current;
        if (start === null) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (Math.hypot(dx, dy) > TOUCH_SLOP_PX) cancelTimer();
      },

      onPointerUp: cancelTimer,
      onPointerCancel: cancelTimer,
      onPointerLeave: cancelTimer,

      onContextMenu: (event) => event.preventDefault(),

      onClick: (event) => {
        if (firedRef.current) {
          // Тем же касанием тап не засчитывается — арена жестов вручную.
          firedRef.current = false;
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        onTap?.();
      },
    };
  }, [onLongPress, onTap]);

  // `dispose`: незавершённый отсчёт не должен пережить размонтирование.
  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    },
    [],
  );

  return handlers;
}
